import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { AfterViewInit, Component, ElementRef, forwardRef, HostBinding, HostListener, Inject, Input, LOCALE_ID, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input-number',
  templateUrl: './input-number.component.html',
  styleUrls: ['./input-number.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputNumberComponent),
      multi: true
    }
  ]
})
export class InputNumberComponent implements ControlValueAccessor, OnChanges, AfterViewInit {

  @Input() value: number;
  @Input() format: 'integer' | 'decimal' = 'decimal';
  @Input() minimumFractionDigits: number = 0;
  @Input() maximumFractionDigits: number = 2;
  @Input() admitNegative: boolean = false;
  @Input() placeholder: string = '';
  @Input() set readonly(value: any) {
    this.isReadonly = value !== null && `${value}` !== 'false';
  }

  @ViewChild('inputElement') inputElement: ElementRef;
  @HostBinding('class.focused') isFocused: boolean = false;

  protected disabled: boolean = false;
  protected isReadonly: boolean = false;
  protected formattedValue: string = '';

  protected onChange: any = () => { }
  protected onTouch: any = () => { }

  constructor(
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngAfterViewInit(): void {
    this.inputElement.nativeElement.value = this.formattedValue;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.value || changes.format) && this.value !== undefined) {
      this.writeInputValue(this.formatValue(this.value));
    }
  }

  writeValue(val: any): void {
    if (this.value !== val) {
      this.value = val;
      if (val === null || val === undefined || val === '') {
        this.writeInputValue('');
      } else {
        if (typeof this.value !== 'number') {
          const parsed = parseFloat(this.value);
          this.value = isNaN(parsed) ? null : parsed;
        }
        this.writeInputValue(this.formatValue(this.value));
      }
      this.onChange(this.value);
      this.onTouch(this.value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: any): void {
    const inputValue = event.target.value;
    const parsedValue = this.parseValue(inputValue);
    const minusSign = getLocaleNumberSymbol(this.locale, NumberSymbol.MinusSign);

    const start = event.target.selectionStart;
    const end = event.target.selectionEnd;

    if (isNaN(parsedValue)) { // && !(this.admitNegative && inputValue === minusSign)
      this.value = null;
      this.onChange(null);
      this.onTouch(null);
      if(this.admitNegative && inputValue.includes(minusSign)) {
        this.writeInputValue(minusSign);
      } else {
        this.writeInputValue('');
      }
    }
    else if (this.format === 'decimal') {
      // Let the user type the decimal separator at the end of the value
      const decimalSeparator = getLocaleNumberSymbol(this.locale, NumberSymbol.Decimal);
      const matchDecimalsEnd = event.target.value.match(new RegExp(`\\${decimalSeparator}(0{0,${this.maximumFractionDigits}})$`));
      if (!matchDecimalsEnd) {
        const matchMoreDecimals = inputValue.match(new RegExp(`\\${decimalSeparator}(0{${this.maximumFractionDigits},}\\d+)$`));
        if (matchMoreDecimals) {
          const length = matchMoreDecimals[1].length - this.maximumFractionDigits;
          event.target.value = event.target.value.substring(0, inputValue.length - length);
        } else {
          this.writeInputValue(this.formatValue(parsedValue));
        }
      } else if (matchDecimalsEnd && end !== inputValue.length) {
        const options: any = {
          useGrouping: true,
          minimumFractionDigits: matchDecimalsEnd[1].length
        };
        this.writeInputValue(new Intl.NumberFormat(this.locale, options).format(parsedValue));
      }

      if (parsedValue !== this.value) {
        this.value = parsedValue;
        this.onChange(this.value);
        this.onTouch(this.value);
      }
    }
    else if (parsedValue === this.value) {
      if (this.format === 'integer') {
        event.target.value = this.formattedValue;
      }
    } else {
      this.writeValue(parsedValue);
    }

    const newStart = start + (event.target.value.length - inputValue.length);
    const newEnd = end + (event.target.value.length - inputValue.length);
    event.target.setSelectionRange(newStart, newEnd);
  }

  onKeyDown(event: KeyboardEvent): void {
    const decimalSeparator = getLocaleNumberSymbol(this.locale, NumberSymbol.Decimal);
    const minusSign = getLocaleNumberSymbol(this.locale, NumberSymbol.MinusSign);
    if (this.inputElement.nativeElement.value.includes(decimalSeparator) &&
      (event.key === decimalSeparator || event.code === 'NumpadDecimal')) {
      // Allows only one decimal separator
      event.preventDefault();
    } else if (event.code === 'NumpadDecimal') {
      // Change NumpadDecimal for actual decimal separator
      event.preventDefault();
      const inputElement = this.inputElement.nativeElement;
      const start = inputElement.selectionStart;
      const end = inputElement.selectionEnd;
      const value = inputElement.value;
      inputElement.value = value.substring(0, start) + decimalSeparator + value.substring(end);
      inputElement.setSelectionRange(start + 1, start + 1);
      this.onInput({ target: inputElement });
    } else if (((this.admitNegative && this.inputElement.nativeElement.selectionStart > 0) ||
      (this.admitNegative && this.inputElement.nativeElement.value.includes(minusSign)) || 
      (!this.admitNegative)) &&
      (event.key === minusSign || event.code === 'NumpadSubtract')) {
      // Allows only one minus sign at start of the value
      event.preventDefault();
    }
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this.isFocused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.isFocused = false;

    let decimalSeparator = null;
    if (this.format === 'decimal' &&
      (decimalSeparator = getLocaleNumberSymbol(this.locale, NumberSymbol.Decimal)) &&
      this.inputElement.nativeElement.value?.match(new RegExp(`\\${decimalSeparator}(0{0,${this.maximumFractionDigits}})$`))
    ) {
      this.writeInputValue(this.formatValue(this.value));
    }
  }

  @HostListener('click')
  onClick(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  /** Method to write a value to the input element */
  private writeInputValue(value: string): void {
    this.formattedValue = value;
    if (this.inputElement) {
      this.inputElement.nativeElement.value = value;
    }
  }

  /** Method to format the value based on the format type */
  private formatValue(value: number): string {
    if (this.format === 'integer') {
      return new Intl.NumberFormat(this.locale, { useGrouping: true }).format(value);
    } else if (this.format === 'decimal') {
      const options: any = {
        useGrouping: true,
        minimumFractionDigits: this.minimumFractionDigits,
        maximumFractionDigits: this.maximumFractionDigits
      };
      return new Intl.NumberFormat(this.locale, options).format(value);
    }

    return value.toString();
  }

  /** Method to parse the formatted value back to a number */
  private parseValue(value: string): number {
    // Remove any non-numeric characters except for the decimal separator and minus sign
    const decimalSeparator = getLocaleNumberSymbol(this.locale, NumberSymbol.Decimal);
    const minusSign = getLocaleNumberSymbol(this.locale, NumberSymbol.MinusSign);
    const cleanedValue = this.admitNegative ? value.replace(new RegExp(`[^0-9\\${decimalSeparator}\\${minusSign}]`, 'g'), '') : value.replace(new RegExp(`[^0-9\\${decimalSeparator}]`, 'g'), '');

    // Replace the locale-specific decimal separator with a dot if decimal or remove it if integer
    let normalizedValue = '';
    if (this.format === 'integer') {
      const decimalIndex = cleanedValue.indexOf(decimalSeparator);
      normalizedValue = decimalIndex === -1 ? cleanedValue : cleanedValue.substring(0, decimalIndex);
    } else {
      normalizedValue = cleanedValue.replace(decimalSeparator, '.');
      const match = normalizedValue.match(new RegExp('\\.(\\d+)$'));
      if (match) {
        const length = match[1].length - this.maximumFractionDigits;
        normalizedValue = normalizedValue.substring(0, normalizedValue.length - length);
      }
    }

    return parseFloat(normalizedValue);
  }

}
